/* global require */
'use_strict';

/**
 * Class to replicate CSS position:sticky behavior.
 * 
 * CSS position:sticky breaks if any ancestor element has overflow set to hidden. This class
 * provides sticky functionality regardless of any ancestor's overflow setting.
 * 
 * To use this class, simply create an element with the class js-sticky and data-sticky-top set to some number
 * (for example, \<div class="js-sticky" data-sticky-top="60">).
 * Sticky blocks work best if they are the only children of their parent elements.
 */
export class StickyBlock {
    /**
     * @var {HTMLElement} sticky_block the element that will have sticky behavior
     * @var {HTMLElement} sticky_container the parent element of the sticky block
     * @var {DOMRect} block_offset object containing displacement values for sticky block relative to the document
     * @var {DOMRect} container_offset object containing displacement values for the sticky container relative to the document
     * @var {number} document_width width of the document. Used when we need to manually check for resizing
     * @var {number} document_height height of the document. Used when we need to manually check for resizing
     * @var {ResizeObserver} resize_observer observer to monitor document, container, or block resizing
     * @var {number} container_min_width last block width written to the container's min-width
     * @var {number} container_min_height last block height written to the container's min-height
     * @var {number} sticky_top last top offset written to the sticky block
     * @var {number} sticky_left last left offset written to the sticky block
     */
    sticky_block: HTMLElement;
    sticky_container: HTMLElement;
    block_offset: DOMRect;
    container_offset: DOMRect;
    document_width: number;
    document_height: number;
    resize_observer: ResizeObserver;
    container_min_width: number;
    container_min_height: number;
    sticky_top: number;
    sticky_left: number;

    /**
     * Instantiate the class, add scroll listener, and initialize the resize observer.
     *
     * @param {HTMLElement} sticky_block the element that will have sticky behavior
     * @return {StickyBlock} setup and ready
     */
    constructor(sticky_block: HTMLElement) {
        this.sticky_block = sticky_block;
        this.sticky_container = sticky_block.parentElement;
        window.addEventListener('scroll', this.update_position.bind(this));

        // Some older browsers don't support ResizeObserver
        if (typeof ResizeObserver !== 'undefined') {
            this.init_resize_observer();
        } else {
            this.init_resize_interval();
        }
    }

    /**
     * Initialize resize observer to monitor document, container, or block resizing.
     *
     * @return {void}
     */
    init_resize_observer(): void {
        this.resize_observer = new ResizeObserver((entries) => {
            // window.requestAnimationFrame prevents 'ResizeObserver loop limit exceeded' error
            // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
            window.requestAnimationFrame(() => {
                if (!Array.isArray(entries) || !entries.length) {
                  return;
                }
                this.update_position();
            });
        });

        window.addEventListener("load", () => {
            this.resize_observer.observe(document.documentElement);
            this.resize_observer.observe(this.sticky_container);
            this.resize_observer.observe(this.sticky_block);
        });
    }

    /**
     * Initialize resize interval to check for document, container, or block resizing every 1s.
     * This is a fallback used when ResizeObserver is not supported by user's browser.
     *
     * @return {void}
     */
    init_resize_interval(): void {
        this.document_height = document.body.clientHeight;
        this.document_width = document.body.clientWidth;
        setInterval(() => {
            if ( // Values returned by getBoundingClientRect and clientHeight can differ by a fraction of a pixel
                Math.round(this.document_height) !== Math.round(document.body.clientHeight) ||
                Math.round(this.block_offset.height) !== Math.round(this.sticky_block.clientHeight) ||
                Math.round(this.container_offset.height) !== Math.round(this.sticky_container.clientHeight)
            ) {
                this.document_height = document.body.clientHeight;
                this.document_width = document.body.clientWidth;
                this.update_position();
            }
        }, 1000);
    }

    /**
     * Update the sticky block's position depending on the window's scroll location.
     *
     * @return {void}
     */
    update_position(): void {
        // Do all reads before any writes; a write followed by a read forces another full style recalc.
        this.block_offset = this.sticky_block.getBoundingClientRect();
        this.container_offset = this.sticky_container.getBoundingClientRect();
        const sticky_style = window.getComputedStyle(this.sticky_block);
        const margin_left = parseFloat(sticky_style.marginLeft);
        const margin_right = parseFloat(sticky_style.marginRight);
        const margin_top = parseFloat(sticky_style.marginTop);
        const margin_bottom = parseFloat(sticky_style.marginBottom);
        const container_style = window.getComputedStyle(this.sticky_container);
        const container_content_width = this.sticky_container.clientWidth
            - parseFloat(container_style.paddingLeft) - parseFloat(container_style.paddingRight);
        const container_content_height = this.sticky_container.clientHeight
            - parseFloat(container_style.paddingTop) - parseFloat(container_style.paddingBottom);

        // The container only needs updating when the block's size changes.
        if (this.block_offset.width !== this.container_min_width || this.block_offset.height !== this.container_min_height) {
            this.container_min_width = this.block_offset.width;
            this.container_min_height = this.block_offset.height;
            this.set_styles(this.sticky_container, {
                position: 'relative',
                minWidth: this.block_offset.width,
                minHeight: this.block_offset.height,
            });
            // The new minimums can only resize the container if the block doesn't already fit inside it,
            // so skip the re-measure (and the style recalc it forces) otherwise.
            if (this.block_offset.width > container_content_width || this.block_offset.height > container_content_height) {
                this.container_offset = this.sticky_container.getBoundingClientRect();
            }
        }

        // Initialize sticky position to top left of viewport
        let sticky_left = -this.container_offset.left;
        let sticky_top = -this.container_offset.top;

        // Add sticky top
        if (this.sticky_block.dataset.stickyTop) {
            sticky_top += parseFloat(this.sticky_block.dataset.stickyTop);
        }

        // Clamp sticky block's top left to container
        if (sticky_left < 0) {
            sticky_left = 0;
        }
        if (sticky_top < 0) {
            sticky_top = 0;
        }

        const sticky_width = this.block_offset.width + margin_left + margin_right;
        const sticky_height = this.block_offset.height + margin_top + margin_bottom;

        // Clamp sticky block's bottom right to container
        if (sticky_left + sticky_width > this.container_offset.width) {
            sticky_left = this.container_offset.width - sticky_width;
        }
        if (sticky_top + sticky_height > this.container_offset.height) {
            sticky_top = this.container_offset.height - sticky_height;
        }

        // Skip the write when nothing moved so an unchanged position doesn't invalidate styles.
        if (sticky_top === this.sticky_top && sticky_left === this.sticky_left) {
            return;
        }
        this.sticky_top = sticky_top;
        this.sticky_left = sticky_left;
        this.set_styles(this.sticky_block, {
            position: 'relative',
            top: sticky_top + 'px',
            left: sticky_left + 'px'
        });

    }

    /**
     * Update the sticky block's styles using the passed object.
     *
     * @param {HTMLElement} element to be styled
     * @param {[key: string]: any} styles css styles to apply to the element
     * @return {void}
     */
    set_styles(element: HTMLElement, styles: {[key: string]: any}): void {
        for (const property in styles) {
            element.style[property] = styles[property];
        }
    }

}

export function init_sticky_block() : void {
    const sticky_blocks = document.getElementsByClassName('js-sticky') as HTMLCollectionOf<HTMLElement>;
    for (const sticky_block of sticky_blocks) {
        (() => new StickyBlock(sticky_block))();
    }
}
